import React, { useEffect, useMemo, useState } from 'react';
import {
  Plus, Save, Archive, Trash2, ChevronDown, ChevronRight,
  History, Globe, Zap, TrendingUp, TrendingDown, RotateCcw,
} from 'lucide-react';
import AppShell from '../components/AppShell';
import axiosInstance from '../utils/axiosInstance';

const CARD_TYPES = ['policy', 'procedure', 'voice', 'product', 'escalation', 'data'];
const STATUSES = ['active', 'draft', 'archived'];

const STATUS_STYLE = {
  active: 'bg-emerald-50 text-emerald-700',
  draft: 'bg-amber-50 text-amber-700',
  archived: 'bg-stone-100 text-stone-500',
};

const emptyCard = () => ({
  title: '',
  content: '',
  card_type: 'policy',
  categories: [],
  triggers: [],
  priority: 100,
  status: 'active',
  source: '',
});

const Knowledge = () => {
  const [cards, setCards] = useState([]);
  const [perf, setPerf] = useState({});
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [creating, setCreating] = useState(false);
  const [statusFilter, setStatusFilter] = useState('active');

  const load = async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const [cardsRes, perfRes, catRes] = await Promise.all([
        axiosInstance.get('api/replai/knowledge/'),
        axiosInstance.get('api/replai/knowledge/performance/'),
        axiosInstance.get('api/replai/categories/'),
      ]);
      setCards(cardsRes.data);
      const byslug = {};
      (perfRes.data || []).forEach((p) => { byslug[p.slug] = p; });
      setPerf(byslug);
      const NON_DRAFTING = ['noise_incoming', 'noise_outgoing', 'unknown'];
      setCategories(
        (catRes.data || [])
          .map((c) => c.name || c.id || c)
          .filter((n) => n && !NON_DRAFTING.includes(n))
      );
    } catch (e) {
      console.error('Failed to load knowledge cards:', e);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const visible = useMemo(() => {
    const list = statusFilter === 'all' ? cards : cards.filter((c) => c.status === statusFilter);
    return [...list].sort((a, b) => (b.priority - a.priority) || a.title.localeCompare(b.title));
  }, [cards, statusFilter]);

  const counts = useMemo(() => {
    const c = { active: 0, draft: 0, archived: 0 };
    cards.forEach((k) => { c[k.status] = (c[k.status] || 0) + 1; });
    return c;
  }, [cards]);

  return (
    <AppShell
      title="Knowledge"
      subtitle="The policies and facts Replai is allowed to state. Cards are scoped to categories and trigger words, versioned on every edit, and tracked for how they perform."
    >
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-1 text-xs">
          {['active', 'draft', 'archived', 'all'].map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`rounded-lg px-3 py-1.5 font-medium capitalize transition ${
                statusFilter === s
                  ? 'bg-foreground text-white'
                  : 'text-muted-foreground hover:bg-[hsl(var(--secondary))]'
              }`}
            >
              {s}{s !== 'all' ? ` · ${counts[s] || 0}` : ''}
            </button>
          ))}
        </div>
        <button
          onClick={() => setCreating(true)}
          className="inline-flex items-center gap-1.5 rounded-xl bg-foreground px-3 py-2 text-sm font-medium text-white hover:opacity-90"
        >
          <Plus className="h-4 w-4" /> New card
        </button>
      </div>

      {creating && (
        <div className="mb-5">
          <CardEditor
            initial={emptyCard()}
            categories={categories}
            isNew
            onCancel={() => setCreating(false)}
            onSaved={() => { setCreating(false); load(); }}
          />
        </div>
      )}

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : loadError ? (
        <div className="text-sm text-muted-foreground">
          Couldn&apos;t load the cards.{' '}
          <button onClick={load} className="font-medium underline hover:opacity-80">
            Retry
          </button>
        </div>
      ) : (
        <div className="space-y-2.5">
          {visible.map((card) => (
            <CardRow
              key={card.id}
              card={card}
              perf={perf[card.slug]}
              categories={categories}
              onChanged={load}
            />
          ))}
          {visible.length === 0 && (
            <p className="text-sm text-muted-foreground">No {statusFilter} cards.</p>
          )}
        </div>
      )}
    </AppShell>
  );
};

const CardRow = ({ card, perf, categories, onChanged }) => {
  const [open, setOpen] = useState(false);
  const scope = card.categories?.length ? card.categories : null;
  const inj = perf?.injected || 0;
  const pos = perf?.feedback_pos || 0;
  const neg = perf?.feedback_neg || 0;

  return (
    <div className="bg-white rounded-2xl border border-border shadow-sm overflow-hidden">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[hsl(var(--secondary)/0.4)]"
      >
        {open ? <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
              : <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium truncate">{card.title}</span>
            <span className={`rounded-full px-2 py-0.5 text-[0.6rem] font-semibold capitalize ${STATUS_STYLE[card.status]}`}>
              {card.status}
            </span>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            {scope ? scope.map((c) => (
              <span key={c} className="inline-flex items-center gap-1 rounded-md bg-sky-50 px-1.5 py-0.5 text-[0.6rem] font-medium text-sky-700">
                {c}
              </span>
            )) : (
              <span className="inline-flex items-center gap-1 rounded-md bg-violet-50 px-1.5 py-0.5 text-[0.6rem] font-medium text-violet-700">
                <Globe className="h-2.5 w-2.5" /> global
              </span>
            )}
            {card.triggers?.length > 0 && (
              <span className="inline-flex items-center gap-1 rounded-md bg-amber-50 px-1.5 py-0.5 text-[0.6rem] font-medium text-amber-700">
                <Zap className="h-2.5 w-2.5" /> {card.triggers.length} trigger{card.triggers.length === 1 ? '' : 's'}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0 text-xs text-muted-foreground">
          <span title="times injected into a draft">{inj}× used</span>
          {pos > 0 && <span className="inline-flex items-center gap-0.5 text-emerald-600"><TrendingUp className="h-3 w-3" />{pos}</span>}
          {neg > 0 && <span className="inline-flex items-center gap-0.5 text-rose-600"><TrendingDown className="h-3 w-3" />{neg}</span>}
          <span className="tabular-nums" title="priority">p{card.priority}</span>
        </div>
      </button>
      {open && (
        <div className="border-t border-border px-4 py-4">
          <CardEditor
            initial={card}
            categories={categories}
            onSaved={onChanged}
          />
        </div>
      )}
    </div>
  );
};

const CardEditor = ({ initial, categories, isNew = false, onCancel, onSaved }) => {
  const [form, setForm] = useState({
    ...initial,
    triggers: (initial.triggers || []).join(', '),
  });
  const [saving, setSaving] = useState(false);
  const [showRevs, setShowRevs] = useState(false);
  const [revs, setRevs] = useState(null);
  const [err, setErr] = useState('');

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleCat = (cat) => setForm((f) => ({
    ...f,
    categories: f.categories.includes(cat)
      ? f.categories.filter((c) => c !== cat)
      : [...f.categories, cat],
  }));

  const payload = () => ({
    title: form.title.trim(),
    content: form.content.trim(),
    card_type: form.card_type,
    categories: form.categories,
    triggers: form.triggers.split(',').map((t) => t.trim()).filter(Boolean),
    priority: Number(form.priority) || 100,
    status: form.status,
    source: form.source || '',
  });

  const save = async () => {
    if (!form.title.trim() || !form.content.trim()) {
      setErr('Title and content are required.');
      return;
    }
    setSaving(true); setErr('');
    try {
      if (isNew) await axiosInstance.post('api/replai/knowledge/', payload());
      else await axiosInstance.patch(`api/replai/knowledge/${initial.id}/`, payload());
      onSaved?.();
    } catch (e) {
      setErr(e?.response?.data ? JSON.stringify(e.response.data) : 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const archive = async () => {
    setSaving(true);
    try {
      await axiosInstance.delete(`api/replai/knowledge/${initial.id}/`);
      onSaved?.();
    } finally { setSaving(false); }
  };

  const loadRevs = async () => {
    if (revs) { setShowRevs((s) => !s); return; }
    const { data } = await axiosInstance.get(`api/replai/knowledge/${initial.id}/revisions/`);
    setRevs(data);
    setShowRevs(true);
  };

  const inputCls = 'w-full rounded-lg border border-border bg-white px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[hsl(var(--ring))]';

  return (
    <div className="space-y-3">
      <input
        className={inputCls}
        placeholder="Title (e.g. Order freeze option)"
        value={form.title}
        onChange={(e) => set('title', e.target.value)}
      />
      <textarea
        className={`${inputCls} min-h-[90px] leading-relaxed`}
        placeholder="The knowledge text Replai may state confidently…"
        value={form.content}
        onChange={(e) => set('content', e.target.value)}
      />

      <div>
        <div className="mb-1.5 text-[0.7rem] font-semibold uppercase tracking-wider text-muted-foreground">
          Applies to categories <span className="font-normal normal-case">(none = global, every ticket)</span>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => toggleCat(cat)}
              className={`rounded-md px-2 py-1 text-[0.7rem] font-medium transition ${
                form.categories.includes(cat)
                  ? 'bg-sky-600 text-white'
                  : 'bg-[hsl(var(--secondary))] text-muted-foreground hover:bg-sky-50'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <div className="mb-1.5 text-[0.7rem] font-semibold uppercase tracking-wider text-muted-foreground">
            Trigger words <span className="font-normal normal-case">(comma-separated; only inject when one appears)</span>
          </div>
          <input
            className={inputCls}
            placeholder="holiday, vacation, away"
            value={form.triggers}
            onChange={(e) => set('triggers', e.target.value)}
          />
        </div>
        <div className="grid grid-cols-3 gap-2">
          <label className="block">
            <span className="mb-1.5 block text-[0.7rem] font-semibold uppercase tracking-wider text-muted-foreground">Type</span>
            <select className={inputCls} value={form.card_type} onChange={(e) => set('card_type', e.target.value)}>
              {CARD_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="mb-1.5 block text-[0.7rem] font-semibold uppercase tracking-wider text-muted-foreground">Status</span>
            <select className={inputCls} value={form.status} onChange={(e) => set('status', e.target.value)}>
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="mb-1.5 block text-[0.7rem] font-semibold uppercase tracking-wider text-muted-foreground">Priority</span>
            <input type="number" className={inputCls} value={form.priority} onChange={(e) => set('priority', e.target.value)} />
          </label>
        </div>
      </div>

      {err && <p className="text-xs text-rose-600">{err}</p>}

      <div className="flex items-center gap-2 pt-1">
        <button
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-1.5 rounded-xl bg-foreground px-3 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
        >
          <Save className="h-4 w-4" /> {isNew ? 'Create card' : 'Save'}
        </button>
        {isNew ? (
          <button onClick={onCancel} className="rounded-xl px-3 py-2 text-sm text-muted-foreground hover:bg-[hsl(var(--secondary))]">
            Cancel
          </button>
        ) : (
          <>
            <button
              onClick={loadRevs}
              className="inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-sm text-muted-foreground hover:bg-[hsl(var(--secondary))]"
            >
              <History className="h-4 w-4" /> History
            </button>
            {form.status !== 'archived' && (
              <button
                onClick={archive}
                className="inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-sm text-rose-600 hover:bg-rose-50 ml-auto"
              >
                <Archive className="h-4 w-4" /> Archive
              </button>
            )}
          </>
        )}
      </div>

      {showRevs && revs && (
        <div className="mt-2 rounded-xl border border-border bg-[hsl(var(--secondary)/0.4)] p-3">
          <div className="mb-2 text-[0.7rem] font-semibold uppercase tracking-wider text-muted-foreground">
            Edit history ({revs.length})
          </div>
          <div className="space-y-2">
            {revs.map((r) => (
              <div key={r.id} className="text-xs">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <span className="font-medium text-foreground">{r.edited_by || 'unknown'}</span>
                  <span>{new Date(r.created_at).toLocaleString()}</span>
                  <span className="italic">{r.change_note}</span>
                </div>
                <div className="mt-0.5 line-clamp-2 text-muted-foreground">{r.content}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Knowledge;
